import React, {useContext, useEffect, useState} from "react";
import styles from "../../screens/variables/views/list_view.module.css";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowRight, faClipboard, faCog, faEyeSlash} from "@fortawesome/free-solid-svg-icons";
import {Link} from "react-router-dom";
import Modal from "../modal/modal";
import {SelectInput, SliderInput, TextInput} from "../input/inputs";
import axiosInstance from "../../AxiosInstance";
import MyContext from "../../Context";
import VerticalInput from "../vertical_input/vertical_input";
import { useTranslation } from 'react-i18next';
import {VARIABLE_TYPE, getVariableType, getStateLabels, getBooleanLabel} from "../utils";


export default function VariableBox({data, name, isDashboardItem, setLocked, allVariables, onHide}) {
    const { t } = useTranslation();
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState({precision: 0, comparison_type: 0, widget_type: 'variable'});
    const [showDashboardModal, setShowDashboardModal] = useState(false);
    const {dashboards} = useContext(MyContext);

    const samplingOptions = [
        { id: 0, name: t('variables.sampling1h') },
        { id: 1, name: t('variables.sampling24h') },
        { id: 2, name: t('variables.sampling7d') }
    ];

    useEffect(() => {
        if(setLocked) setLocked(showModal);
    }, [showModal]);

    useEffect(() => {
        setForm({
            ...form,
            precision: data['precision'],
            green_1: data['low_threshold'],
            yellow_1: data['medium_threshold'],
            green_2: data['low_threshold_2'],
            yellow_2: data['medium_threshold_2'],
            high_active: data['high_active'],
            medium_active: data['medium_active'],
            low_active: data['low_active'],
            disable_alerts: data['disable_alerts'] || false,
            bool_alert_value: data['bool_alert_value'],
            bool_alert_type: data['bool_alert_type'],
            true_label: data['true_label'] || '',
            false_label: data['false_label'] || '',
            is_production_counter: data['is_production_counter'] || false,
            production_counter_type: data['production_counter_type'] ?? 0,
            production_counter_role: data['production_counter_role'] || 'good',
            is_consumption_counter: data['is_consumption_counter'] || false,
            consumption_counter_type: data['consumption_counter_type'] ?? 1,
            consumption_aggregation: data['consumption_aggregation'] ?? 0,
        });
    }, [data]);

    const variableType = getVariableType(data);
    const isBoolean = variableType === VARIABLE_TYPE.BOOLEAN;
    const isString = variableType === VARIABLE_TYPE.STRING;
    const isNumeric = !isBoolean && !isString;
    // Etiquetas heredadas del tipo de dispositivo: se usan como placeholder del
    // formulario, para que se vea qué mostrará si no se pone un override.
    const inheritedLabels = getStateLabels({variable: data.variable});

    // Compute which production counter roles are already taken by OTHER variables on the same device
    const takenRoles = (allVariables || [])
        .filter(v => v.id !== data.id && v.device_id === data.device_id && v.is_production_counter && v.production_counter_role)
        .map(v => v.production_counter_role);

    const allRoleOptions = [
        {id: 'good', name: t('variables.goodProduction')},
        {id: 'bad', name: t('variables.badProduction')}
    ];

    // Filter out taken roles (but keep the current variable's own role)
    const availableRoleOptions = allRoleOptions.filter(
        opt => !takenRoles.includes(opt.id) || opt.id === data.production_counter_role
    );

    // Check if both roles are fully taken by OTHER variables (not this one)
    const bothRolesTaken = !data.is_production_counter && takenRoles.includes('good') && takenRoles.includes('bad');
    const goodVar = (allVariables || []).find(v => v.id !== data.id && v.is_production_counter && v.production_counter_role === 'good');
    const badVar = (allVariables || []).find(v => v.id !== data.id && v.is_production_counter && v.production_counter_role === 'bad');

    // Only show 'graph' option for numeric variables (not boolean or string)
    const widgetTypeOptions = isBoolean || isString
        ? [{ id: 'variable', name: t('variables.variableValue') }]
        : [
            { id: 'variable', name: t('variables.variableValue') },
            { id: 'gauge', name: t('variables.gauge') },
            { id: 'graph', name: t('variables.graph') },
            ...(data.is_production_counter ? [{ id: 'production_histogram', name: t('variables.productionHistogram') }] : []),
            ...(data.is_consumption_counter ? [{ id: 'consumption_histogram', name: t('variables.consumptionHistogram') }] : [])
        ];

    const setNewVariable = () => {
        localStorage.setItem('variable', JSON.stringify(data));
        localStorage.setItem('variable_id', data.id);
    }

    const parsedVariable = (value) => {
        // Limitar precisión a máximo 4 decimales
        // Usar ?? para que precision=0 no caiga al default
        const precision = Math.min(Number(value.precision ?? 2), 4);
        const unit = value.variable?.unit || '';
        const rawValue = value.value;

        // Detectar tipo: si el tipo es 0 o undefined, inferir del valor
        const varType = getVariableType(value);

        // Función auxiliar para formatear números con precisión limitada
        const formatNumber = (val, prec) => {
            const num = parseFloat(val);
            if (isNaN(num)) return val;
            return num.toFixed(Math.min(prec, 4));
        };

        if (varType === 1) {
            // INT - entero sin decimales
            return (
                <div>{parseInt(rawValue)}<b>{unit}</b></div>
            )
        }
        else if (varType === 2) {
            // FLOAT - decimal con precisión limitada a 4
            return (
                <div>{formatNumber(rawValue, precision)}<b>{unit}</b></div>
            )
        }
        else if (varType === 3) {
            // STRING - badge/etiqueta coloreada
            return (
                <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: '#e0e7ff',
                    color: '#3730a3',
                    padding: '4px 12px',
                    borderRadius: '16px',
                    fontSize: '14px',
                    fontWeight: 600,
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}>
                    {rawValue}{unit && <span style={{marginLeft: '4px', fontWeight: 400, fontSize: '12px'}}>{unit}</span>}
                </div>
            )
        }
        else if (varType === 4) {
            // BATTERY - decimal con precisión limitada a 4
            return (
                <div>{formatNumber(rawValue, precision)}<b>{unit}</b></div>
            )
        }
        else if (varType === VARIABLE_TYPE.BOOLEAN) {
            // BOOLEAN - LED indicator (green/red circle) + etiqueta de estado.
            // La etiqueta sale de la variable (ej. Abierto/Cerrado) y cae a
            // 'True'/'False' si nadie configuró nada.
            const {isTrue, label} = getBooleanLabel(value, rawValue);
            return (
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <div style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: isTrue ? '#22c55e' : '#ef4444',
                        boxShadow: isTrue
                            ? '0 0 8px rgba(34, 197, 94, 0.6)'
                            : '0 0 8px rgba(239, 68, 68, 0.6)',
                        border: `2px solid ${isTrue ? '#16a34a' : '#dc2626'}`
                    }}/>
                    <span style={{fontWeight: 500}}>{label}</span>
                </div>
            )
        }
        else {
            // DEFAULT - si es número, formatear con máximo 4 decimales
            const numValue = parseFloat(rawValue);
            if (!isNaN(numValue) && isFinite(numValue)) {
                return (
                    <div>{formatNumber(rawValue, precision)}<b>{unit}</b></div>
                )
            }
            return (
                <div>{rawValue}<b>{unit}</b></div>
            )
        }
    }

    const saveVariableSettings = () => {
        const id = data.id;
        // Transformar los nombres del form a los nombres del modelo
        const payload = {
            precision: form.precision,
            low_threshold: form.green_1,
            medium_threshold: form.yellow_1,
            low_threshold_2: form.green_2,
            medium_threshold_2: form.yellow_2,
            disable_alerts: form.disable_alerts || false,
            bool_alert_value: form.bool_alert_value,
            bool_alert_type: form.bool_alert_type,
            true_label: (form.true_label || '').trim(),
            false_label: (form.false_label || '').trim(),
            is_production_counter: form.is_production_counter || false,
            production_counter_type: form.is_production_counter ? form.production_counter_type : null,
            production_counter_role: form.is_production_counter ? form.production_counter_role : null,
            is_consumption_counter: form.is_consumption_counter || false,
            consumption_counter_type: form.is_consumption_counter ? form.consumption_counter_type : null,
            consumption_aggregation: form.is_consumption_counter ? form.consumption_aggregation : null,
        };
        axiosInstance.put('variable_data/' + id + '/', payload).then(() => {
            window.location.reload();
        });
    }

    const addToDashboard = () => {
        let widgetType;
        if (form.widget_type === 'graph') widgetType = 3;
        else if (form.widget_type === 'production_histogram') widgetType = 6;
        else if (form.widget_type === 'gauge') widgetType = 7;
        else widgetType = 2;
        const payload = {
            "name": form.widget_name,
            "key": data.id,
            "type": widgetType
        };
        // Include sampling for graph and histogram widgets
        if ((widgetType === 3 || widgetType === 6) && form.widget_sampling !== null && form.widget_sampling !== undefined && form.widget_sampling !== '') {
            payload.widget_sampling = parseInt(form.widget_sampling);
        }
        axiosInstance.post(`dashboard/${form.dashboard_id}/add_widget/`, payload).then((res) => {
            window.location.reload();
        })
    }

    // Determinar qué semáforo debe estar encendido según el estado actual
    const getCurrentStatus = () => {
        if (data.high_active) return 'high';
        if (data.medium_active) return 'medium';
        if (data.low_active) return 'low';
        return 'none'; // Sin estado determinado
    }

    const currentStatus = getCurrentStatus();

    return (
        <div className={`${styles.variable_box}`}>
            <Modal show={showDashboardModal} setter={setShowDashboardModal} name={t('dashboard.addWidget')} onClick={addToDashboard} buttonName={t('common.create')}>
                <div className={'mb-3'}>
                    <SelectInput name={t('dashboard.title')} internalName={'dashboard_id'} form={form} setForm={setForm} options={dashboards}/>
                    <SelectInput name={t('variables.widgetTypeLabel')} internalName={'widget_type'} form={form} setForm={setForm} options={widgetTypeOptions}/>
                    {form.widget_type === 'graph' && (
                        <SelectInput name={t('variables.samplingLabel')} internalName={'widget_sampling'} form={form} setForm={setForm} options={samplingOptions}/>
                    )}
                    <TextInput name={t('common.name')} internalName={'widget_name'} form={form} setForm={setForm}/>
                </div>
            </Modal>

            <Modal show={showModal} setter={setShowModal} name={t('variables.settings')} buttonName={t('common.save')} onClick={saveVariableSettings}>
                <div className={`${styles.modal} mb-4`}>
                    {/* Decimal precision - only for numeric types, not for boolean or string */}
                    {!isBoolean && !isString && (
                        <>
                            <div><b>{t('variables.decimalPrecision')}</b></div>
                            <div style={{fontSize: "x-large"}}><b>{form.precision}</b></div>
                            <SliderInput
                                min={0}
                                max={4}
                                step={1}
                                name={'precision'}
                                form={form}
                                setForm={setForm}
                            />
                            <div className={'mt-4 mb-4'} style={{height: "1px", backgroundColor: "#CBD5E1"}}/>
                        </>
                    )}

                    {/* String type has no limit configuration */}
                    {isString && (
                        <div style={{padding: '16px', backgroundColor: '#f0f9ff', borderRadius: '8px', marginBottom: '16px'}}>
                            <div style={{color: '#0369a1', fontWeight: 500}}>
                                {t('variables.stringVarDesc')}
                            </div>
                        </div>
                    )}

                    {/* Disable alerts - not for strings */}
                    {!isString && (
                        <div className={'mb-3'}>
                            <label style={{display: 'flex', alignItems: 'center', cursor: 'pointer'}}>
                                <input
                                    type="checkbox"
                                    checked={form.disable_alerts || false}
                                    onChange={(e) => setForm({...form, disable_alerts: e.target.checked})}
                                    style={{marginRight: '8px', width: '18px', height: '18px'}}
                                />
                                <span>{t('variables.disableAlerts')}</span>
                            </label>
                        </div>
                    )}

                    {/* Etiquetas de estado. Depende SÓLO de isBoolean, nunca de
                        disable_alerts: cómo se llama un estado es independiente
                        de si esa variable genera alertas. */}
                    {isBoolean && (
                        <div className={'mb-3'}>
                            <div><b>{t('variables.stateLabels')}</b></div>
                            <div style={{marginBottom: '8px', color: '#64748b', fontSize: '13px'}}>
                                {t('variables.stateLabelsHint')}
                            </div>
                            <div style={{display: 'flex', gap: '12px'}}>
                                <div style={{flex: 1}}>
                                    <TextInput name={t('variables.trueLabel')} internalName={'true_label'}
                                               placeholder={inheritedLabels.trueLabel}
                                               form={form} setForm={setForm}/>
                                </div>
                                <div style={{flex: 1}}>
                                    <TextInput name={t('variables.falseLabel')} internalName={'false_label'}
                                               placeholder={inheritedLabels.falseLabel}
                                               form={form} setForm={setForm}/>
                                </div>
                            </div>
                            <div className={'mt-4 mb-4'} style={{height: "1px", backgroundColor: "#CBD5E1"}}/>
                        </div>
                    )}

                    {/* Boolean limit configuration */}
                    {isBoolean && !form.disable_alerts && (
                        <div>
                            <div><b>{t('variables.boolAlertConfig')}</b></div>
                            <div style={{marginTop: '12px'}}>
                                <div style={{marginBottom: '8px', color: '#64748b'}}>
                                    {t('variables.boolAlertSelect')}
                                </div>
                                <div style={{display: 'flex', gap: '12px', marginBottom: '16px'}}>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '8px 16px',
                                        border: form.bool_alert_value === true ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        backgroundColor: form.bool_alert_value === true ? '#eff6ff' : '#fff'
                                    }}>
                                        <input
                                            type="radio"
                                            name="bool_alert_value"
                                            checked={form.bool_alert_value === true}
                                            onChange={() => setForm({...form, bool_alert_value: true, bool_alert_type: form.bool_alert_type || 1})}
                                            style={{marginRight: '8px'}}
                                        />
                                        {t('variables.trueAlert')}
                                    </label>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '8px 16px',
                                        border: form.bool_alert_value === false ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        backgroundColor: form.bool_alert_value === false ? '#eff6ff' : '#fff'
                                    }}>
                                        <input
                                            type="radio"
                                            name="bool_alert_value"
                                            checked={form.bool_alert_value === false}
                                            onChange={() => setForm({...form, bool_alert_value: false, bool_alert_type: form.bool_alert_type || 1})}
                                            style={{marginRight: '8px'}}
                                        />
                                        {t('variables.falseAlert')}
                                    </label>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '8px 16px',
                                        border: form.bool_alert_value === null || form.bool_alert_value === undefined ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        backgroundColor: form.bool_alert_value === null || form.bool_alert_value === undefined ? '#eff6ff' : '#fff'
                                    }}>
                                        <input
                                            type="radio"
                                            name="bool_alert_value"
                                            checked={form.bool_alert_value === null || form.bool_alert_value === undefined}
                                            onChange={() => setForm({...form, bool_alert_value: null, bool_alert_type: null})}
                                            style={{marginRight: '8px'}}
                                        />
                                        {t('variables.noAlert')}
                                    </label>
                                </div>

                                {form.bool_alert_value !== null && form.bool_alert_value !== undefined && (
                                    <div>
                                        <div style={{marginBottom: '8px', color: '#64748b'}}>
                                            {t('variables.alertType')}
                                        </div>
                                        <div style={{display: 'flex', gap: '12px'}}>
                                            <label style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '8px 16px',
                                                border: Number(form.bool_alert_type) === 1 ? '2px solid #ef4444' : '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                backgroundColor: Number(form.bool_alert_type) === 1 ? '#fef2f2' : '#fff'
                                            }}>
                                                <input
                                                    type="radio"
                                                    name="bool_alert_type"
                                                    checked={Number(form.bool_alert_type) === 1}
                                                    onChange={() => setForm({...form, bool_alert_type: 1})}
                                                    style={{marginRight: '8px'}}
                                                />
                                                <span style={{color: '#ef4444', fontWeight: 600}}>{t('variables.danger')}</span>
                                            </label>
                                            <label style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '8px 16px',
                                                border: Number(form.bool_alert_type) === 2 ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                backgroundColor: Number(form.bool_alert_type) === 2 ? '#fffbeb' : '#fff'
                                            }}>
                                                <input
                                                    type="radio"
                                                    name="bool_alert_type"
                                                    checked={Number(form.bool_alert_type) === 2}
                                                    onChange={() => setForm({...form, bool_alert_type: 2})}
                                                    style={{marginRight: '8px'}}
                                                />
                                                <span style={{color: '#f59e0b', fontWeight: 600}}>{t('variables.warning')}</span>
                                            </label>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Numeric threshold limits - only for numeric types */}
                    {!isBoolean && !isString && !form.disable_alerts && (
                        <div>
                            <div><b>{t('variables.thresholdLimits')}</b></div>
                            <VerticalInput form={form} setForm={setForm}/>
                        </div>
                    )}

                    {/* Production Counter Configuration - only for numeric types, not boolean */}
                    {isNumeric && !form.is_consumption_counter && (
                        <>
                            <div className={'mt-4 mb-4'} style={{height: "1px", backgroundColor: "#CBD5E1"}}/>
                            <div className={'mb-3'}>
                                <label style={{display: 'flex', alignItems: 'center', cursor: bothRolesTaken ? 'not-allowed' : 'pointer', opacity: bothRolesTaken ? 0.5 : 1}}>
                                    <input
                                        type="checkbox"
                                        checked={form.is_production_counter || false}
                                        disabled={bothRolesTaken}
                                        onChange={(e) => setForm({...form, is_production_counter: e.target.checked})}
                                        style={{marginRight: '8px', width: '18px', height: '18px'}}
                                    />
                                    <span><b>{t('variables.productionCounter')}</b></span>
                                </label>
                                {bothRolesTaken && (
                                    <div style={{marginTop: '8px', padding: '10px 12px', backgroundColor: '#fef3c7', borderRadius: '6px', fontSize: '13px', color: '#92400e'}}>
                                        {t('variables.bothRolesTaken', {goodVar: goodVar?.name || goodVar?.variable?.name, badVar: badVar?.name || badVar?.variable?.name})}
                                    </div>
                                )}
                            </div>
                            {form.is_production_counter && (
                                <>
                                    <SelectInput
                                        name={t('variables.counterRole')}
                                        internalName={'production_counter_role'}
                                        form={form}
                                        setForm={setForm}
                                        options={availableRoleOptions}
                                    />
                                    <SelectInput
                                        name={t('variables.counterType')}
                                        internalName={'production_counter_type'}
                                        form={form}
                                        setForm={setForm}
                                        options={[
                                            {id: 0, name: t('variables.dailyResetCounter')},
                                            {id: 1, name: t('variables.accumulativeCounter')},
                                            {id: 2, name: t('variables.pulseCounter')}
                                        ]}
                                    />
                                </>
                            )}
                        </>
                    )}

                    {/* Consumption Counter Configuration - only for numeric types, not when production counter is active */}
                    {isNumeric && !form.is_production_counter && (
                        <>
                            <div className={'mt-4 mb-4'} style={{height: "1px", backgroundColor: "#CBD5E1"}}/>
                            <div className={'mb-3'}>
                                <label style={{display: 'flex', alignItems: 'center', cursor: 'pointer'}}>
                                    <input
                                        type="checkbox"
                                        checked={form.is_consumption_counter || false}
                                        onChange={(e) => setForm({...form, is_consumption_counter: e.target.checked})}
                                        style={{marginRight: '8px', width: '18px', height: '18px'}}
                                    />
                                    <span><b>{t('variables.consumptionCounter')}</b></span>
                                </label>
                                <div style={{marginTop: '4px', fontSize: '12px', color: '#6b7280'}}>
                                    {t('variables.consumptionCounterDesc')}
                                </div>
                            </div>
                            {form.is_consumption_counter && (
                                <>
                                    <SelectInput
                                        name={t('variables.counterType')}
                                        internalName={'consumption_counter_type'}
                                        form={form}
                                        setForm={setForm}
                                        options={[
                                            {id: 0, name: t('variables.dailyResetCounter')},
                                            {id: 1, name: t('variables.accumulativeCounter')}
                                        ]}
                                    />
                                    <SelectInput
                                        name={t('variables.aggregation')}
                                        internalName={'consumption_aggregation'}
                                        form={form}
                                        setForm={setForm}
                                        options={[
                                            {id: 0, name: t('variables.sumAggregation')},
                                            {id: 1, name: t('variables.avgAggregation')}
                                        ]}
                                    />
                                </>
                            )}
                        </>
                    )}
                </div>
            </Modal>

            <div className={styles.variable_header}>
                <div className={styles.variable_info}>
                    <div className={styles.variable_name}>{name ? name : (data.name ? data.name : data.variable.name)}</div>
                    <div className={styles.variable_value}>{parsedVariable(data)}</div>
                </div>
                {!isDashboardItem && (
                    <div className={styles.variable_actions}>
                        <FontAwesomeIcon
                            role={'button'}
                            icon={faClipboard}
                            onClick={() => {
                                const variableName = data.name || data.variable?.name || '';
                                const device = JSON.parse(localStorage.getItem('device'));
                                const deviceName = device?.name || '';
                                const defaultName = deviceName ? `${variableName} - ${deviceName}` : variableName;
                                setForm({...form, widget_name: defaultName, widget_type: 'variable', widget_sampling: ''});
                                setShowDashboardModal(true);
                            }}
                            color={'#94a3b8'}
                            style={{cursor: 'pointer'}}
                        />
                        <FontAwesomeIcon
                            icon={faCog}
                            color={'#94a3b8'}
                            onClick={() => setShowModal(true)}
                            role={'button'}
                            style={{cursor: 'pointer'}}
                        />
                        <Link to={'/graphics/show'} onClick={() => setNewVariable()}>
                            <FontAwesomeIcon role='button' icon={faArrowRight} color={'#94a3b8'}/>
                        </Link>
                        {onHide && (
                            <FontAwesomeIcon
                                icon={faEyeSlash}
                                color={'#94a3b8'}
                                title={'Hide variable'}
                                onClick={() => {
                                    axiosInstance.post(`variable_data/${data.id}/hide/`).then(() => {
                                        onHide(data.id);
                                    });
                                }}
                                role={'button'}
                                style={{cursor: 'pointer'}}
                            />
                        )}
                    </div>
                )}
            </div>
            {/* Semaphore display - different based on variable type */}
            {/* String: no semaphore */}
            {/* Boolean: 2-state semaphore (alert type + OK) */}
            {/* Numeric: 3-state semaphore (danger, warning, OK) */}
            {!data.disable_alerts && !isString && (
                <>
                    {isBoolean ? (
                        /* Boolean: 2-state semaphore */
                        <div className={styles.variable_status_row}>
                            {Number(data.bool_alert_type) === 1 ? (
                                /* Danger + OK */
                                <>
                                    <div className={`${styles.variable_severity_container} ${currentStatus !== 'high' && styles.variable_disabled}`} style={{backgroundColor: currentStatus === 'high' ? "#FFE2E5" : "#f8fafc"}}>
                                        <img src={`${currentStatus === 'high' ? '/alerts/high.png' : '/alerts/high_disabled.png' }`} alt={'high'} height={'18px'}/>
                                        <div className={styles.variable_severity_text}>{t('variables.danger')}</div>
                                    </div>
                                    <div className={`${styles.variable_severity_container} ${currentStatus !== 'low' && styles.variable_disabled}`} style={{backgroundColor: currentStatus === 'low' ? "#DCFCE7" : "#f8fafc"}}>
                                        <img src={`${currentStatus === 'low' ? '/alerts/low.png' : '/alerts/low_disabled.png' }`} alt={'low'} height={'18px'}/>
                                        <div className={styles.variable_severity_text}>{t('common.ok')}</div>
                                    </div>
                                </>
                            ) : Number(data.bool_alert_type) === 2 ? (
                                /* Warning + OK */
                                <>
                                    <div className={`${styles.variable_severity_container} ${currentStatus !== 'medium' && styles.variable_disabled}`} style={{backgroundColor: currentStatus === 'medium' ? "#FFF4DE" : "#f8fafc"}}>
                                        <img src={`${currentStatus === 'medium' ? '/alerts/medium.png' : '/alerts/medium_disabled.png' }`} alt={'medium'} height={'18px'}/>
                                        <div className={styles.variable_severity_text}>{t('variables.warning')}</div>
                                    </div>
                                    <div className={`${styles.variable_severity_container} ${currentStatus !== 'low' && styles.variable_disabled}`} style={{backgroundColor: currentStatus === 'low' ? "#DCFCE7" : "#f8fafc"}}>
                                        <img src={`${currentStatus === 'low' ? '/alerts/low.png' : '/alerts/low_disabled.png' }`} alt={'low'} height={'18px'}/>
                                        <div className={styles.variable_severity_text}>{t('common.ok')}</div>
                                    </div>
                                </>
                            ) : (
                                /* No alert configured - just show OK */
                                <div className={`${styles.variable_severity_container}`} style={{backgroundColor: "#DCFCE7"}}>
                                    <img src={'/alerts/low.png'} alt={'low'} height={'18px'}/>
                                    <div className={styles.variable_severity_text}>{t('common.ok')}</div>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* Numeric: 3-state semaphore */
                        <div className={styles.variable_status_row}>
                            <div className={`${styles.variable_severity_container} ${currentStatus !== 'high' && styles.variable_disabled}`} style={{backgroundColor: currentStatus === 'high' ? "#FFE2E5" : "#f8fafc"}}>
                                <img src={`${currentStatus === 'high' ? '/alerts/high.png' : '/alerts/high_disabled.png' }`} alt={'high'} height={'18px'}/>
                                <div className={styles.variable_severity_text}>{t('variables.danger')}</div>
                            </div>
                            <div className={`${styles.variable_severity_container} ${currentStatus !== 'medium' && styles.variable_disabled}`} style={{backgroundColor: currentStatus === 'medium' ? "#FFF4DE" : "#f8fafc"}}>
                                <img src={`${currentStatus === 'medium' ? '/alerts/medium.png' : '/alerts/medium_disabled.png' }`} alt={'medium'} height={'18px'}/>
                                <div className={styles.variable_severity_text}>{t('variables.warning')}</div>
                            </div>
                            <div className={`${styles.variable_severity_container} ${currentStatus !== 'low' && styles.variable_disabled}`} style={{backgroundColor: currentStatus === 'low' ? "#DCFCE7" : "#f8fafc"}}>
                                <img src={`${currentStatus === 'low' ? '/alerts/low.png' : '/alerts/low_disabled.png' }`} alt={'low'} height={'18px'}/>
                                <div className={styles.variable_severity_text}>{t('common.ok')}</div>
                            </div>
                        </div>
                    )}
                </>
            )}

        </div>
    );
}